import { type Ref, useEffectEvent, useLayoutEffect } from "react";
import ActionSheet, {
  type ActionSheetButton,
} from "@/components/shared/ActionSheet";
import LinkData, { linkTypes } from "@/plugin/LinkData";
import { eventNames, valueNames } from "@/modules/Constants";
import ShopWindowHeader from "@/components/shopwindow/ShopWindowHeader";
import type { ExperienceId } from "@/types/experience";
import type { ExperienceState } from "@/types/state";

const REGION = "Merchant CTA Action Sheet";

interface ActiveArticle {
  clusterName: string;
  cindex: number;
  index: number;
}

interface ArticleUrl {
  idmerchant: ExperienceId;
  url: string;
}

interface MerchantButton extends ActionSheetButton {
  link: LinkData;
  idmerchant: ExperienceId;
}

type MerchantCtaActionSheetProps = {
  experienceState: ExperienceState;
  hasHeader?: boolean;
  nodeRef?: Ref<HTMLDivElement>;
  onApplicationEvent?: (event: Record<string, unknown>) => void;
};

export default function MerchantCtaActionSheet({
  experienceState,
  hasHeader,
  nodeRef,
  onApplicationEvent,
}: MerchantCtaActionSheetProps) {
  const experienceData = experienceState.experienceData;
  const applicationData = experienceState.applicationData;

  const onExit = () => {
    if (onApplicationEvent) {
      const clusterIndex = applicationData.getValue(
        valueNames.ACTIVE_ARTICLE,
      ) as ActiveArticle;

      onApplicationEvent({
        name: eventNames.ACTION_SHEET_EXIT,
        region: REGION,
        clusterName: clusterIndex.clusterName,
        cindex: clusterIndex.cindex,
        index: clusterIndex.index,
      });
    }
  };

  // The window listener stays installed from mount; it must see the latest props.
  const onWindowClick = useEffectEvent((e: MouseEvent) => {
    if (!e.defaultPrevented) {
      e.preventDefault();
      onExit();
    }
  });

  useLayoutEffect(() => {
    window.addEventListener("click", onWindowClick);
    return () => {
      window.removeEventListener("click", onWindowClick);
    };
  }, []);

  const getButtonData = () => {
    const clusterIndex = applicationData.getValue(
      valueNames.ACTIVE_ARTICLE,
    ) as ActiveArticle;
    const article = experienceData.getArticle(
      clusterIndex.clusterName,
      clusterIndex.cindex,
      clusterIndex.index,
    ) as { urls?: ArticleUrl[] };
    const urls = article.urls;
    const ret: MerchantButton[] = [];

    for (let n = 0, max = urls ? urls.length : 0; n < max; n++) {
      const url = (urls as ArticleUrl[])[n];
      const merchant = experienceData.getMerchant(url.idmerchant);
      if (merchant) {
        ret.push({
          imageUrl: experienceData.getMerchantLogoUrl(merchant.idmerchant),
          label: typeof merchant.name === "string" ? merchant.name : undefined,
          link: new LinkData(
            linkTypes.URL,
            url.url,
            `${clusterIndex.clusterName}.cta`,
            true,
          ),
          idmerchant: url.idmerchant,
        });
      }
    }

    return ret;
  };

  const onButtonClick = (button: ActionSheetButton) => {
    const clusterIndex = applicationData.getValue(
      valueNames.ACTIVE_ARTICLE,
    ) as ActiveArticle;
    const { link, idmerchant } = button as MerchantButton;

    if (onApplicationEvent) {
      if (clusterIndex && link) {
        onApplicationEvent({
          region: REGION,
          name: eventNames.LINK,
          clusterName: clusterIndex.clusterName,
          cindex: clusterIndex.cindex,
          index: clusterIndex.index,
          link: link,
          idmerchant: idmerchant,
        });
      }
    }
  };

  const header = hasHeader ? (
    <ShopWindowHeader
      src={experienceData.getLogoImageUrl()}
      hasExitButton={true}
      onExit={onExit}
    />
  ) : null;

  return (
    <div
      className="action-sheet-wrapper tw:[.landscape_&]:bg-[rgba(255,255,255,0.6)] tw:[.portrait_&]:flex tw:[.portrait_&]:flex-col tw:[.portrait_&]:bg-[#fff]"
      ref={nodeRef}
    >
      {header}
      <ActionSheet
        onButtonClick={onButtonClick}
        title="Shop online with"
        buttons={getButtonData()}
        hasExitButton={applicationData.isLandscape()}
        onExit={onExit}
      />
    </div>
  );
}
